import { useEffect, useState } from "react";

const FALLBACK_WORDS = [
  "Acadmax — ERP",
  "Unicrm.ai",
  "TeachingSoftware.ai",
  "HelloSir.ai",
  "Studyportal.ai",
  "Visainterviews.ai",
];

// Kept exported for legacy imports; live rotator now uses the `words` prop.
export const WORDS = FALLBACK_WORDS;

/**
 * Typewriter that types → pauses → deletes → moves to next word.
 * Rendered as a "fill in the blank" underline so the missing word looks native.
 *
 * words         — array of strings to cycle through (defaults to FALLBACK_WORDS)
 * onWordChange  — fired every time a new word starts typing (idx, word)
 */
export default function Typewriter({ words, onWordChange }) {
  const list = (words && words.length ? words : FALLBACK_WORDS);
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing");

  // Guard against `list` shrinking below the current index after a live CMS update
  useEffect(() => {
    if (idx >= list.length) setIdx(0);
  }, [list, idx]);

  useEffect(() => {
    if (onWordChange) onWordChange(idx, list[idx]);
  }, [idx, onWordChange, list]);

  useEffect(() => {
    const current = list[idx] || "";
    let t;
    if (phase === "typing") {
      if (text.length < current.length) {
        t = setTimeout(() => setText(current.slice(0, text.length + 1)), 70);
      } else {
        t = setTimeout(() => setPhase("deleting"), 1800);
      }
    } else if (phase === "deleting") {
      if (text.length > 0) {
        t = setTimeout(() => setText(current.slice(0, text.length - 1)), 32);
      } else {
        setPhase("typing");
        setIdx((i) => (i + 1) % list.length);
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, list]);

  return (
    <span
      className="relative inline-block align-baseline"
      style={{ minWidth: "min(14ch, 46vw)" }}
      data-testid="hero-typewriter"
    >
      <span
        className="italic font-medium relative inline-block pb-1 text-white"
        style={{
          // Soft warm cream → light amber wash so the word pops against the video
          background: "linear-gradient(90deg, #FFFFFF 0%, #FFF3D0 55%, #FFC48A 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          textShadow: "0 2px 24px rgba(0,0,0,0.35)",
        }}
      >
        {text || "\u00A0"}
      </span>

      {/* blinking cursor */}
      <span
        aria-hidden="true"
        className="inline-block w-[3px] h-[0.85em] align-baseline ml-1 -mb-1 rounded-sm"
        style={{
          background: "linear-gradient(180deg, #FFFFFF, #FFC48A)",
          animation: "tw-blink 1s steps(2) infinite",
        }}
      />

      {/* underline blank — clean white with a soft glow so it reads on the video */}
      <span
        aria-hidden="true"
        className="absolute left-0 right-0 bottom-0 h-[3px] rounded-full"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,240,200,0.9) 55%, rgba(255,196,138,0.95) 100%)",
          boxShadow: "0 0 12px rgba(255,255,255,0.35)",
        }}
      />

      <style>{`
        @keyframes tw-blink {
          0%, 49% { opacity: 1; }
          50%,100% { opacity: 0; }
        }
      `}</style>
    </span>
  );
}
